import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { History, Save, RotateCcw, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, FieldLabel, Pills, Stat } from "@/components/Bits";
import { SearchSelect } from "@/components/SearchSelect";
import { TrioInputs } from "@/components/TrioInputs";
import { useApp } from "@/store/AppStore";
import { useAuth } from "@/store/AuthStore";
import { SYSTEMS, BOLT_TYPES, getBoltDiameters, getBoltLengths, getRate } from "@/data/boltData";
import { NUT_SPECS, getNutSizes, getNutRate } from "@/data/nutData";
import { computeTrio } from "@/lib/calc";
import { fmtNum, fmtInt, fmtBags, fmtDia, fmtLen, fmtNutSize, boltFullLabel } from "@/lib/format";

const firstTypeOf = (system) => BOLT_TYPES.find((t) => t.system === system)?.id;

export function CalcPage() {
  const { recents, addRecent, clearRecents } = useApp();
  const { user } = useAuth();
  const [mode, setMode] = useState("bolt");
  const [bolt, setBolt] = useState({ system: "inches", typeId: "in_full", dia: "", len: "" });
  const [nut, setNut] = useState({ specId: "bsw", size: "" });
  const [field, setField] = useState("weight");
  const [raw, setRaw] = useState("");

  const rate = useMemo(() => {
    if (mode === "bolt") return getRate(bolt.typeId, bolt.dia, bolt.len);
    return getNutRate(nut.specId, nut.size);
  }, [mode, bolt, nut]);

  const values = useMemo(() => computeTrio(field, raw, rate || 0), [field, raw, rate]);
  const onEdit = (key, val) => {
    setField(key);
    setRaw(val);
  };

  const diaOpts = useMemo(
    () => getBoltDiameters(bolt.typeId).map((d) => ({ value: d, label: fmtDia(bolt.system, d) })),
    [bolt.typeId, bolt.system]
  );
  const lenOpts = useMemo(
    () => getBoltLengths(bolt.typeId, bolt.dia).map((l) => ({ value: l, label: fmtLen(bolt.system, l), sub: `${getRate(bolt.typeId, bolt.dia, l)}/50kg` })),
    [bolt.typeId, bolt.dia, bolt.system]
  );
  const nutOpts = useMemo(
    () => getNutSizes(nut.specId).map((s) => ({ value: s, label: fmtNutSize(nut.specId, s), sub: `${getNutRate(nut.specId, s)}/50kg` })),
    [nut.specId]
  );

  const setSystem = (system) => setBolt({ system, typeId: firstTypeOf(system), dia: "", len: "" });
  const setType = (typeId) => setBolt((b) => ({ ...b, typeId, dia: "", len: "" }));
  const setDia = (dia) => setBolt((b) => ({ ...b, dia, len: "" }));
  const setLen = (len) => setBolt((b) => ({ ...b, len }));

  const typeOpts = BOLT_TYPES.filter((t) => t.system === bolt.system).map((t) => ({ value: t.id, label: t.name }));

  const label = mode === "bolt" ? boltFullLabel(bolt) : `${fmtNutSize(nut.specId, nut.size)} NUT · ${NUT_SPECS.find((s) => s.id === nut.specId)?.label}`;

  const save = () => {
    if (!user) return toast.error("Sign in as a member to save calculations");
    if (!rate) return toast.error("Pick a full spec first");
    addRecent({ mode, label, rate, field, raw, bolt, nut });
    toast.success("Calculation saved");
  };

  const loadRecent = (r) => {
    setMode(r.mode);
    if (r.bolt) setBolt(r.bolt);
    if (r.nut) setNut(r.nut);
    setField(r.field || "weight");
    setRaw(r.raw || "");
    toast.success("Loaded");
  };

  const fields = [
    { key: "weight", label: "Weight", unit: "KG", testid: "input-weight-kg", fmt: (n) => fmtNum(n, 2) },
    { key: "bags", label: "Bags", unit: "BAG", testid: "input-bags", fmt: (n) => fmtNum(n, 2) },
    { key: "pieces", label: "Pieces", unit: "PCS", testid: "input-pieces", fmt: (n) => fmtInt(n) },
  ];

  return (
    <div className="space-y-4">
      <Pills
        testid="bolt-nut-toggle"
        value={mode}
        onChange={setMode}
        options={[{ value: "bolt", label: "🔩 BOLT" }, { value: "nut", label: "⬡ NUT" }]}
      />

      <Card>
        {mode === "bolt" ? (
          <div className="space-y-4">
            <div>
              <FieldLabel>System</FieldLabel>
              <Pills testid="calc-system" value={bolt.system} onChange={setSystem} options={SYSTEMS.map((s) => ({ value: s.id, label: s.label }))} />
            </div>
            <div>
              <FieldLabel>Thread Type</FieldLabel>
              <Pills testid="calc-type" value={bolt.typeId} onChange={setType} options={typeOpts} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Diameter</FieldLabel>
                <SearchSelect testid="calc-diameter" value={bolt.dia} onChange={setDia} options={diaOpts} placeholder="Dia" searchPlaceholder="Search diameter…" />
              </div>
              <div>
                <FieldLabel>Length</FieldLabel>
                <SearchSelect testid="calc-length" value={bolt.len} onChange={setLen} options={lenOpts} placeholder="Length" disabled={!bolt.dia} searchPlaceholder="Search length…" />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <FieldLabel>Nut Spec</FieldLabel>
              <Pills
                testid="calc-nut-spec"
                value={nut.specId}
                onChange={(specId) => setNut({ specId, size: "" })}
                options={NUT_SPECS.map((s) => ({ value: s.id, label: s.label, sub: s.standard }))}
              />
            </div>
            <div>
              <FieldLabel>Size</FieldLabel>
              <SearchSelect testid="calc-nut-size" value={nut.size} onChange={(size) => setNut((n) => ({ ...n, size }))} options={nutOpts} placeholder="Nut size" searchPlaceholder="Search size…" />
            </div>
          </div>
        )}
      </Card>

      <Card className={rate ? "border-orange-500/40" : ""}>
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Enter any value</div>
          {rate ? (
            <span className="rounded-full bg-orange-500/15 px-3 py-1 font-mono text-xs font-bold text-orange-300" data-testid="rate-display">
              {fmtInt(rate)} pcs / 50kg
            </span>
          ) : (
            <span className="text-xs text-slate-500">select full spec</span>
          )}
        </div>
        <TrioInputs fields={fields} values={values} field={field} raw={raw} onEdit={onEdit} disabled={!rate} />
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Stat label="Bags (50 kg)" value={fmtBags(values.bags)} testid="bags-display-result" accent />
          <Stat label="Total Weight" value={fmtNum(values.weight, 1)} unit="KG" testid="calc-weight-result" />
        </div>
        <div className="mt-3 flex gap-2">
          {user && (
            <Button data-testid="save-recent-calc-btn" onClick={save} className="flex-1 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">
              <Save className="mr-1.5 h-4 w-4" /> Save Calc
            </Button>
          )}
          <Button variant="outline" onClick={() => { setRaw(""); setField("weight"); }} className="rounded-xl border-slate-700 text-slate-300">
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {user && (
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            <History className="h-4 w-4" /> Recent ({recents.length})
          </div>
          {recents.length > 0 && (
            <button onClick={clearRecents} className="text-xs font-semibold text-slate-500 hover:text-red-400" data-testid="clear-recents-btn">
              Clear
            </button>
          )}
        </div>
        {recents.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-600">No saved calculations yet.</p>
        ) : (
          <div className="space-y-2">
            {recents.map((r) => (
              <motion.button
                key={r.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => loadRecent(r)}
                data-testid={`recent-item-${r.id}`}
                className="flex w-full items-center justify-between rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5 text-left hover:border-orange-500/40"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-100">{r.label}</div>
                  <div className="font-mono text-[11px] text-slate-500">{r.raw || 0} {r.field} · {fmtInt(r.rate)}/50kg</div>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-orange-400" />
              </motion.button>
            ))}
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
